import React, { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import {
  LayoutDashboard, Users, User, Wallet, Key, Package, MessageSquare, Image,
  FileText, BarChart2, CreditCard, History, BookOpen, Bell, LogOut,
  Sun, Moon, Globe, ChevronLeft, ChevronRight, ShieldCheck, ShieldOff, Menu, X, AlertCircle
} from 'lucide-react';

const MENUS = {
  developer: [
    { key: 'overview',         icon: LayoutDashboard, labelKey: 'overview' },
    { key: 'admins',           icon: Users,           labelKey: 'adminUsers' },
    { key: 'ngos',             icon: User,            labelKey: 'ngoUsers' },
    { key: 'wallet',           icon: Wallet,          labelKey: 'masterWallet' },
    { key: 'api-keys',         icon: Key,             labelKey: 'apiKeys' },
    { key: 'bundle-settings',  icon: Package,         labelKey: 'bundleSettings' },
    { key: 'email-templates',  icon: MessageSquare,   labelKey: 'emailTemplates' },
    { key: 'logo',             icon: Image,           labelKey: 'logoSettings' },
    { key: 'blocked-numbers',  icon: ShieldOff,       labelKey: 'blockedNumbers' },
    { key: 'credit-report',    icon: FileText,        labelKey: 'kycReports' },
    { key: 'na-overview',      icon: AlertCircle,     labelKey: 'naOverview' },
    { key: 'pull-analytics',   icon: BarChart2,       labelKey: 'pullAnalytics' },
    { key: 'audit-logs',       icon: BarChart2,       labelKey: 'auditLogs' },
    { key: 'email-logs',       icon: Bell,            labelKey: 'emailLogs' },
  ],
  admin: [
    { key: 'overview', icon: LayoutDashboard, labelKey: 'overview' },
    { key: 'purchase', icon: CreditCard, labelKey: 'purchaseCredits' },
    { key: 'ngos', icon: Users, labelKey: 'ngoUsers' },
    { key: 'credit-report', icon: FileText, labelKey: 'kycReports' },
    { key: 'na-reports', icon: AlertCircle, labelKey: 'naReports' },
    { key: 'usage-logs', icon: BarChart2, labelKey: 'usageLogs' },
    { key: 'purchase-history', icon: History, labelKey: 'purchaseHistory' },
  ],
  ngo: [
    { key: 'overview', icon: LayoutDashboard, labelKey: 'overview' },
    { key: 'credit-report', icon: FileText, labelKey: 'kycReports' },
    { key: 'usage-logs', icon: BarChart2, labelKey: 'usageLogs' },
    { key: 'ledger', icon: BookOpen, labelKey: 'creditLedger' },
  ]
};

export default function Sidebar({ role, activeTab, setActiveTab, logoSettings = {} }) {
  const { t, toggleTheme, toggleLanguage, theme, language, logout, user } = useApp();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Apply sidebar-surface class for light-mode depth
  const sidebarClass = `hidden lg:flex flex-col fixed inset-y-0 left-0 z-40 bg-card border-r border-border sidebar-surface transition-all duration-300 ${collapsed ? 'w-14' : 'w-64'}`;

  const menuItems = MENUS[role] || MENUS.ngo;
  const logoSize = logoSettings.logo_size === 'small' ? 'h-7' : logoSettings.logo_size === 'large' ? 'h-14' : 'h-10';
  const txtSzMap = { xs:'text-xs', sm:'text-sm', base:'text-base', lg:'text-lg', xl:'text-xl' };
  const logoTxtClass = txtSzMap[logoSettings.logo_text_size || 'sm'] || 'text-sm';
  const logoTxtStyle = logoSettings.logo_text_color ? { color: logoSettings.logo_text_color } : {};

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-border min-h-[64px] sidebar-logo-area">
        {logoSettings.logo_url ? (
          <div className="flex items-center gap-2 min-w-0">
            <img src={logoSettings.logo_url} alt="logo" className={`${logoSize} object-contain flex-shrink-0`} />
            {!collapsed && logoSettings.app_name && (
              <span className={`font-semibold ${logoTxtClass} truncate`} style={logoTxtStyle}>
                {logoSettings.app_name}
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={16} className="text-primary" />
            </div>
            {!collapsed && (
              <span className={`font-semibold ${logoTxtClass} truncate`} style={logoTxtStyle}>
                {logoSettings.app_name || 'Credit Platform'}
              </span>
            )}
          </div>
        )}
        <button onClick={() => setCollapsed(!collapsed)}
          className="ml-auto p-1 rounded hover:bg-secondary text-muted-foreground hidden lg:block">
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
        {menuItems.map(item => (
          <button
            key={item.key}
            data-testid={`nav-${item.key}`}
            onClick={() => { setActiveTab(item.key); setMobileOpen(false); }}
            className={`sidebar-item w-full ${activeTab === item.key ? 'active' : ''}`}
          >
            <item.icon size={16} className="flex-shrink-0" />
            {!collapsed && <span className="truncate">{t(item.labelKey)}</span>}
          </button>
        ))}
      </nav>

      {/* Bottom controls */}
      <div className="border-t border-border p-3 space-y-1">
        <div className="flex items-center gap-2 px-2 py-1">
          <button
            data-testid="sidebar-theme-toggle"
            onClick={toggleTheme}
            className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {theme === 'dark' ? <Sun size={13} /> : <Moon size={13} />}
            {!collapsed && <span>{theme === 'dark' ? t('lightMode') : t('darkMode')}</span>}
          </button>
          <button
            data-testid="sidebar-lang-toggle"
            onClick={toggleLanguage}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground ml-auto transition-colors"
          >
            <Globe size={13} />
            {!collapsed && <span>{language === 'en' ? 'தமிழ்' : 'EN'}</span>}
          </button>
        </div>
        {!collapsed && user && (
          <div className="px-2 py-1.5 rounded-lg bg-secondary/50">
            <p className="text-xs font-medium text-foreground truncate">{user.name || 'User'}</p>
            <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
          </div>
        )}
        <button
          data-testid="logout-btn"
          onClick={logout}
          className="sidebar-item w-full text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <LogOut size={15} className="flex-shrink-0" />
          {!collapsed && <span>{t('logout')}</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
        <button onClick={() => setMobileOpen(true)}>
          <Menu size={20} />
        </button>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium capitalize">{activeTab.replace(/-/g, ' ')}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={toggleTheme} className="p-1.5 rounded hover:bg-secondary">
            {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <button onClick={toggleLanguage} className="text-xs px-2 py-1 rounded border border-border">
            {language === 'en' ? 'தமிழ்' : 'EN'}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-card border-r border-border">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <span className="font-semibold text-sm">Menu</span>
              <button onClick={() => setMobileOpen(false)}><X size={18} /></button>
            </div>
            <SidebarContent />
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className={sidebarClass}>
        <SidebarContent />
      </div>
    </>
  );
}
